import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  ICompetition,
  ICompetitionCreate,
  ICompetitionCreateResponse,
  ICompetitionWithLabel,
  ICompetitions,
} from '@pssa/types';

import { competitionLabel } from '@/helpers/competitionLabel.ts';
import { xhr } from '@/helpers/service.ts';

import { useAuthenticationStore } from './useAuthenticationStore';
import { useResultsStore } from './useResultsStore';

export const useCompetitionStore = defineStore('competition', () => {
  const authStore = useAuthenticationStore();
  const resultStore = useResultsStore();
  const competitions = ref<ICompetitionWithLabel[]>([]);
  const currentCompetition = ref<ICompetitionWithLabel>();
  const currentCompetitionId = ref('');
  const isLoading = ref(false);

  const isDraftCompetition = computed(
    () => !currentCompetition.value?.published && authStore.isAuthorised,
  );

  async function initalise(competitionId?: string | string[]) {
    await authStore.checkAuthentication();
    await getCompetitions();
    currentCompetition.value = competitions.value[0];

    currentCompetitionId.value =
      competitionId && !Array.isArray(competitionId)
        ? competitionId
        : currentCompetition.value?._id;

    changeCompetition(currentCompetitionId.value);
  }

  async function changeCompetition(id: string) {
    resultStore.resetResults();

    const competition = competitions.value.find(
      (competition: ICompetition) => competition._id === id,
    );

    if (competition) {
      currentCompetition.value = competition;
      currentCompetitionId.value = id;

      resultStore.getResults(id);
    }
  }

  async function getCompetitions() {
    let isPublished = '';

    if (authStore.isAuthorised) {
      competitions.value = [];
      isPublished = '?published=false';
    }

    if (competitions.value.length === 0) {
      isLoading.value = true;
      const response: ICompetitions = await xhr(`/competitions${isPublished}`);

      if (response) {
        isLoading.value = false;
        competitions.value = response.map((competition) => ({
          ...competition,
          label: competitionLabel(competition),
        }));
      }
    }
  }

  async function deleteCompetition() {
    await xhr(`/competitions/${currentCompetitionId.value}`, {
      method: 'delete',
    });
  }

  async function publishCompetition(published: boolean = false) {
    if (currentCompetition.value) {
      currentCompetition.value.published = published;
    }

    await xhr(`/competitions/${currentCompetitionId.value}/publish`, {
      method: 'put',
      body: { published },
    });
  }

  async function createCompetition(body: ICompetitionCreate) {
    const response: ICompetitionCreateResponse = await xhr('/competitions', {
      method: 'post',
      body,
    });

    const competition = {
      ...response,
      label: competitionLabel(response),
    };

    competitions.value.push(competition);
    return competition;
  }

  async function updateCompetition(body: ICompetitionWithLabel) {
    const response: ICompetition = await xhr(`/competitions/${currentCompetitionId.value}`, {
      method: 'put',
      body,
    });

    const competition = {
      ...response,
      label: competitionLabel(response),
    };
    const competitionIndex = competitions.value.findIndex(
      (competition) => competition.id === body.id,
    );

    competitions.value[competitionIndex] = {
      ...competition,
      id: response.id,
      _id: competitions.value[competitionIndex]._id,
    };

    currentCompetition.value = competitions.value[competitionIndex];
  }

  return {
    changeCompetition,
    competitions,
    createCompetition,
    currentCompetition,
    currentCompetitionId,
    deleteCompetition,
    getCompetitions,
    initalise,
    isDraftCompetition,
    isLoading,
    publishCompetition,
    updateCompetition,
  };
});
